import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeftIcon, ArrowRightIcon, VolumeIcon, RefreshCwIcon, CheckCircleIcon, XIcon } from 'lucide-react';
interface Flashcard {
  id: number;
  en: string;
  ar: string;
  pronunciation?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
}
interface FlashcardPracticeProps {
  cards: Flashcard[];
}
export function FlashcardPractice({
  cards
}: FlashcardPracticeProps) {
  const {
    isArabic
  } = useLanguage();
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [completedCards, setCompletedCards] = useState<Set<number>>(new Set());
  const [showFront, setShowFront] = useState(true);
  const currentCard = cards[currentCardIndex];
  const progress = completedCards.size / cards.length * 100;
  const nextCard = () => {
    if (currentCardIndex < cards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setFlipped(false);
    }
  };
  const prevCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
      setFlipped(false);
    }
  };
  const resetCards = () => {
    setCurrentCardIndex(0);
    setFlipped(false);
    setCompletedCards(new Set());
  };
  const toggleFlip = () => {
    setFlipped(!flipped);
  };
  const markComplete = () => {
    setCompletedCards(prev => new Set([...prev, currentCard.id]));
    nextCard();
  };
  const toggleLanguage = () => {
    setShowFront(!showFront);
  };
  const playSound = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      window.speechSynthesis.speak(utterance);
    }
  };
  return <div className="max-w-md mx-auto" data-id="element-416">
      <div className="flex justify-between items-center mb-6" data-id="element-417">
        <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-418">
          {isArabic ? `البطاقة ${currentCardIndex + 1} من ${cards.length}` : `Card ${currentCardIndex + 1} of ${cards.length}`}
        </div>
        <div className="flex items-center gap-3" data-id="element-419">
          <button onClick={toggleLanguage} className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" data-id="element-420">
            {showFront ? isArabic ? 'عربي → إنجليزي' : 'EN → AR' : isArabic ? 'إنجليزي → عربي' : 'AR → EN'}
          </button>
          <button onClick={resetCards} className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" aria-label="Reset cards" data-id="element-421">
            <RefreshCwIcon className="w-5 h-5" data-id="element-422" />
          </button>
        </div>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full mb-6" data-id="element-423">
        <div className="h-full bg-blue-600 dark:bg-blue-400 rounded-full transition-all duration-300" style={{
        width: `${progress}%`
      }} data-id="element-424" />
      </div>
      <div className={`relative h-64 w-full perspective-1000 cursor-pointer ${flipped ? 'flipped' : ''}`} onClick={toggleFlip} data-id="element-425">
        <div className={`absolute inset-0 w-full h-full transform transition-transform duration-500 ${flipped ? 'rotate-y-180' : ''}`} data-id="element-426">
          {/* Front of card */}
          <div className="absolute inset-0 w-full h-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 flex flex-col items-center justify-center backface-hidden" data-id="element-427">
            <div className="text-3xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-428">
              {showFront ? currentCard.en : currentCard.ar}
            </div>
            {showFront && currentCard.pronunciation && <div className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-429">
                /{currentCard.pronunciation}/
              </div>}
            {showFront && <button onClick={e => {
            e.stopPropagation();
            playSound(currentCard.en);
          }} className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" aria-label="Play pronunciation" data-id="element-430">
                <VolumeIcon className="w-5 h-5" data-id="element-431" />
              </button>}
            <div className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-id="element-432">
              {isArabic ? 'انقر للكشف' : 'Click to reveal'}
            </div>
          </div>
          {/* Back of card */}
          <div className="absolute inset-0 w-full h-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 flex flex-col items-center justify-center backface-hidden rotate-y-180" data-id="element-433">
            <div className="text-3xl font-bold text-gray-900 dark:text-white mb-4" dir={!showFront ? 'ltr' : 'rtl'} data-id="element-434">
              {!showFront ? currentCard.en : currentCard.ar}
            </div>
            {!showFront && currentCard.pronunciation && <div className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-435">
                /{currentCard.pronunciation}/
              </div>}
            {!showFront && <button onClick={e => {
            e.stopPropagation();
            playSound(currentCard.en);
          }} className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" aria-label="Play pronunciation" data-id="element-436">
                <VolumeIcon className="w-5 h-5" data-id="element-437" />
              </button>}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-6" data-id="element-438">
        <button onClick={prevCard} disabled={currentCardIndex === 0} className={`p-2 rounded-full ${currentCardIndex === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} aria-label="Previous card" data-id="element-439">
          <ArrowLeftIcon className="w-6 h-6" data-id="element-440" />
        </button>
        <div className="flex gap-2" data-id="element-441">
          <button onClick={() => {
          toggleFlip();
          setTimeout(() => nextCard(), 500);
        }} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors" data-id="element-442">
            {isArabic ? 'تخطي' : 'Skip'}
          </button>
          <button onClick={markComplete} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2" data-id="element-443">
            <CheckCircleIcon className="w-4 h-4" data-id="element-444" />
            <span data-id="element-445">{isArabic ? 'أعرف هذا' : 'I know this'}</span>
          </button>
        </div>
        <button onClick={nextCard} disabled={currentCardIndex === cards.length - 1} className={`p-2 rounded-full ${currentCardIndex === cards.length - 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} aria-label="Next card" data-id="element-446">
          <ArrowRightIcon className="w-6 h-6" data-id="element-447" />
        </button>
      </div>
      <style jsx data-id="element-448">{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
      `}</style>
    </div>;
}